import { Component, Element, Host, h, Prop, State, Event, EventEmitter, Listen, Method, AttachInternals } from '@stencil/core';
import { generateGuid, generateEventDetailPayload, EventDetail } from '../../utils';
import { IconName } from '../icon/icons';

@Component({
  tag: 'anchor-input',
  styleUrl: 'input.scss',
  shadow: true,
  formAssociated: true,
})
export class Input {
  @Element() host: HTMLElement;

  @AttachInternals() internals: ElementInternals;

  inputElm!: HTMLInputElement;

  /**
   * The value in the input field. Use this to set a value and read it
   */
  @Prop({ mutable: true, reflect: true }) value: string | number = '';

  /**
   * The name of the input field
   */
  @Prop() name: string;

  /**
   * The id of the input field
   */
  @Prop() inputId: string = `anchor-input-${generateGuid()}`;

  /**
   * Set the type for the input field
   */
  @Prop() type: string = 'text';

  /**
   * Placeholder text is shown until the user interacts with the input field
   */
  @Prop() placeholder: string = '';

  /**
   * Set the field to be read only
   */
  @Prop() readonly: boolean = false;

  /**
   * Disable the input field
   */
  @Prop() disabled: boolean = false;

  /**
   * Mark the field as required
   */
  @Prop() required: boolean = false;

  /**
   * Set the autocomplete attribute for the input field
   */
  @Prop() autocomplete: string;

  /**
   * Disable the input field
   */
  @Prop() hideSpinButtons: boolean = false;

  /**
   * Show an error message
   */
  @Prop() errorProp: string = '';

  /**
   * Show an error state on input field
   */
  @Prop() errorField: boolean = false;

  /**
   * Show a warning message
   */
  @Prop() warning: string = '';

  /**
   * Show a help text
   */
  @Prop() helpText: string = '';

  /**
   * This prop should be used when you don't give the input a label, it will disable the margin of the input field
   */
  @Prop() showLabel: boolean = true;

  /**
   * Set a fixed width in characters for the Input field.
   */
  @Prop() size: number = null;

  /**
   * Set a max width in characters for the Input field.
   */
  @Prop() maxLength: number;

  /**
   * Set a minimum number for the Input field.
   */
  @Prop() min: number;

  /**
   * Set a maximum number for the Input field.
   */
  @Prop() max: number;
  
  /**
   * Set the step value for the Input field.
   */
  @Prop() step: number;

  /**
   * Add an info icon with a tooltip to the right of the label
   */
  @Prop() tooltipText: string = '';

  /**
   * A pattern to validate the input field against
   * Note that the pattern does not restrict the input field, it only validates the input when the form is submitted
   */
  @Prop({ reflect: true, mutable: true }) pattern: string;

  /**
   * The inputmode attribute for the input field
   */
  @Prop() inputmode: string;

  /**
   * Add an icon at the right side of the input field, by putting in the name of an Anchor icon
   */
  @Prop() icon: IconName;

  /**
   * Should the icon be inside or outside the input field
   */
  @Prop() iconPlacement: 'inside' | 'outside' = 'inside';

  /**
   * This prop decides if the messages should render between the label of an input and the input field, or below the input field.
   *
   * @type {'inside' | 'below'}
   * @default 'below'
   */
  @Prop({ reflect: true }) messagePlacement: 'inside' | 'below' = 'inside';

  /**
   * aria-live attribute for the input field
   */
  @Prop() ariaLive: string;

  /**
   * aria-describedby attribute for the input field
   */
  @Prop() ariaDescribedBy: string = '';

  /**
   * aria-activedescendant attribute for the input field
   */
  @Prop() ariaActivedescendant: string = '';

  /**
   * This prop decides if all content inside the input should be selected when it gets focus
   */
  @Prop({ reflect: true }) selectOnFocus: boolean;

  /**
   * Show a checkmark icon when the input field has been succesfully filled
   */
  @Prop() filled: boolean = false;

  /**
   * Event emitted when the user is inputting something into the input field
   */
  @Event() inputevent: EventEmitter<EventDetail>;

  /**
   * Event emitted when the user is focusing the input field
   */
  @Event() focusevent: EventEmitter;

  /**
   * Event emitted when the user blurs the input field
   */
  @Event() blurevent: EventEmitter;

  /**
   * Event emitted when the user clears the input field
   */
  @Event() clearevent: EventEmitter<EventDetail>;

  @Listen('input')
  handleInput(event: InputEvent) {
    if (!this.inputElm.value.length) {
      this.clearevent.emit(generateEventDetailPayload(this.inputElm, this.inputElm.value, event));
    }
    this.internals.setFormValue(this.inputElm.value);
    return this.inputevent.emit(generateEventDetailPayload(this.inputElm, this.inputElm.value, event));
  }

  @Listen('paste')
  handlePaste(e) {
    return this.inputevent.emit(e);
  }

  @State() hasErrorSlotActive: boolean = false;

  /**
   *  Event emitted when the input is changed. Only triggers when focus leaves the input
   */
  @Event() inputChange: EventEmitter<EventDetail>;

  handleChange(event: Event) {
    this.inputChange.emit(generateEventDetailPayload(this.inputElm, this.inputElm.value, event));
  }

  @Method()
  async focusInput(timeout = 100) {
    setTimeout(() => {
      this.inputElm.focus();
    }, timeout);
  }

  handleFocus(e: FocusEvent) {
    this.focusevent.emit(e);
    if (this.selectOnFocus) {
      this.inputElm.setSelectionRange(0, (this.inputElm.value ?? '').toString().length);
    }
  }

  handleIconClick(_e: MouseEvent) {
    /** If the icon is inside the input field, it should not block clicking the input. */
    if (this.iconPlacement === 'inside') {
      this.focusInput(1);
    }
  }

  componentWillLoad() {
    this.hasErrorSlotActive = !!this.host.querySelector("[slot='error-msg']");
  }

  componentWillRender() {
    const hasErrorSlotActive = !!this.host.querySelector("[slot='error-msg']");
    if (this.hasErrorSlotActive !== hasErrorSlotActive) {
      this.hasErrorSlotActive = hasErrorSlotActive;
    }
  }

  render() {
    const hasError = Boolean(this.errorProp || this.hasErrorSlotActive);

    const containerClass = {
      'anchor-c-input__container': true,
      'error': hasError,
    };

    const labelClass = {
      'anchor-c-label': true,
    };

    const inputClass = {
      'anchor-c-input': true,
      'anchor-c-input--warning': Boolean(this.warning),
      'anchor-c-input--no-spin-button': this.hideSpinButtons,
      'anchor-c-input--error': hasError || this.errorField,
      [`anchor-c-input--with-icon-${this.iconPlacement}`]: Boolean(this.icon || this.filled),
      'anchor-c-input--set-width': Boolean(this.size),
    };

    const iconClass = {
      'anchor-c-input__icon': true,
      [`anchor-c-input__icon-${this.iconPlacement}`]: true,
    };
    return (
      <Host>
        <div class={containerClass}>
          {this.showLabel && (
            <div class="anchor-c-input__heading">
              <anchor-label for={this.inputId} class={labelClass}>
                <div class="anchor-c-input__label">
                  <div class="anchor-c-input__label-text">
                    <slot />
                  </div>
                </div>
              </anchor-label>
              <slot name="label-suffix">
                {this.tooltipText ? (
                  <div class="anchor-c-input__label-suffix">
                    <anchor-icon id="info" name="info" size="small" />
                    <anchor-tooltip text={this.tooltipText} position="top" target="#info" trigger="click" />
                  </div>
                ) : (
                  ''
                )}
              </slot>
            </div>
          )}
          {this.helpText ? <span class="anchor-c-input__help-msg">{this.helpText}</span> : ''}

          {hasError && (
            <span id={`${this.inputId}__messages`} class={`anchor-c-input__messages anchor-c-input__messages--${this.messagePlacement}`}>
              <span class="anchor-c-input__error-msg">
                {this.hasErrorSlotActive ? <slot name="error-msg"></slot> : this.errorProp}
                <anchor-icon color="inherit" size="small" name="alert-circle"></anchor-icon>
              </span>
            </span>
          )}
          <div class="anchor-c-input__wrapper">
            <div class={inputClass}>
              <div class="anchor-c-input__prefix">
                <slot name='prefix'></slot>
              </div>
              <input
                id={this.inputId}
                aria-required={this.required}
                aria-live={this.ariaLive}
                aria-describedby={this.ariaDescribedBy || `${this.inputId}__messages`}
                aria-activedescendant={this.ariaActivedescendant}
                disabled={this.disabled}
                autocomplete={this.autocomplete || undefined}
                inputmode={this.inputmode}
                maxLength={this.maxLength}
                min={this.min}
                max={this.max}
                name={this.name}
                onChange={(event) => this.handleChange(event)}
                onFocus={e => this.handleFocus(e)}
                onBlur={e => this.blurevent.emit(e)}
                pattern={this.pattern}
                placeholder={this.placeholder}
                readonly={this.readonly}
                size={this.size}
                type={this.type}
                value={this.value}
                ref={el => {
                  this.inputElm = el as HTMLInputElement;
                }}
              />
            </div>
            {this.filled && <anchor-icon onClick={e => this.handleIconClick(e)} name="check" size="small" class={iconClass}></anchor-icon>}
            {this.icon && !this.filled && <anchor-icon onClick={e => this.handleIconClick(e)} name={this.icon} size="small" class={iconClass}></anchor-icon>}
          </div>
        </div>
      </Host>
    );
  }
}
